import React, { useCallback, useEffect, useState } from 'react';
import { billingService } from '../../services/billingService';
import { metricsService } from '../../services/metricsService';
import { PLAN_TITLES } from '../../lib/billing';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { HEAD_ROW, TH, TD, ROW, BADGE, TITLE, HINT, EMPTY, ERROR_BOX, NOTE_BOX, ACCENT_BTN } from './adminStyles';

/**
 * Организации в админке: список и три действия, которых нет у клиента.
 *
 * 🔴 Список здесь главное, а не приложение к формам. Первая версия вкладки
 * состояла из одних форм, и это сразу вышло боком: две ручки принимают
 * идентификатор организации, а взять его было негде — он показывался один раз
 * в ответе на создание. Администратор заводил организацию и терял её из виду
 * в ту же секунду (21.09.2026).
 */
const STATE_LABEL = { active: 'работает', frozen: 'тариф закончился' };
const STATE_BADGE = { active: 'bg-emerald-100 text-emerald-700', frozen: 'bg-paper-track text-paper-ink-3' };

export default function OrganizationsTab() {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState({ owner_email: '', name: '', inn: '' });
  const [member, setMember] = useState('');
  const [newOwner, setNewOwner] = useState('');
  const [note, setNote] = useState(null);
  const [error, setError] = useState(null);
  // Состав выбранной организации и расход по людям (Р11): клиент просит
  // «посмотрите, кто у нас», а список отдаёт только счётчики.
  const [members, setMembers] = useState(null);
  const [usage, setUsage] = useState(null);

  const loadMembers = useCallback(async (org) => {
    if (!org) { setMembers(null); setUsage(null); return; }
    const [m, u] = await Promise.all([
      billingService.adminGetOrgMembers(org.id).catch(() => null),
      // У замороженной тарифа нет — ручка ответит 409; расход тогда не показываем.
      org.state === 'active' ? metricsService.getOrgUsage(org.id).catch(() => null) : null,
    ]);
    setMembers(m ? m.items : null);
    setUsage(u);
  }, []);

  useEffect(() => { loadMembers(selected); }, [selected, loadMembers]);

  const load = useCallback(async (needle) => {
    try {
      const data = await billingService.adminGetOrganizations(
        needle ? { search: needle } : {});
      setRows(data.items || []);
      setTotal(data.total_count || 0);
    } catch (e) {
      setError('Не удалось загрузить список организаций');
    }
  }, []);

  useEffect(() => { load(''); }, [load]);

  const run = async (fn, ok) => {
    setError(null); setNote(null);
    try {
      setNote(ok(await fn()));
      await load(search);
      await loadMembers(selected);
    } catch (e) {
      setError((e && e.response && e.response.data && e.response.data.detail)
        || 'Не удалось выполнить действие');
    }
  };

  const field = (value, onChange, placeholder, label) => (
    <Input value={value} onChange={(e) => onChange(e.target.value)}
           placeholder={placeholder} aria-label={label} className="max-w-xs" />
  );

  return (
    <div data-testid="admin-organizations" className="flex flex-col gap-8 text-sm">
      <section>
        <div className="flex items-center gap-3 mb-3">
          <h3 className={TITLE}>Организации</h3>
          <span className={HINT}>всего {total}</span>
          <div className="flex-1" />
          <Input
            value={search}
            onChange={(e) => { setSearch(e.target.value); load(e.target.value); }}
            placeholder="Название или почта плательщика"
            aria-label="Поиск организации"
            className="max-w-xs"
          />
        </div>

        {rows.length === 0 ? (
          <div className={EMPTY}>Организаций пока нет.</div>
        ) : (
          <Card className="overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className={HEAD_ROW}>
                    <th className={TH}>Название</th><th className={TH}>Плательщик</th><th className={TH}>Тариф</th>
                    <th className={TH}>Состояние</th><th className={TH}>Людей</th><th className={TH}> </th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((o) => (
                    <tr key={o.id} className={`${ROW} ${selected?.id === o.id ? 'bg-paper-panel' : ''}`}>
                      <td className={`${TD} font-medium`}>{o.name}</td>
                      <td className={`${TD} text-paper-ink-3`}>{o.owner_email}</td>
                      <td className={TD}>{PLAN_TITLES[o.plan_code] || o.plan_code || '—'}</td>
                      <td className={TD}>
                        <span className={`${BADGE} ${STATE_BADGE[o.state] || 'bg-paper-bg text-paper-ink-2'}`}>
                          {STATE_LABEL[o.state] || o.state}
                        </span>
                      </td>
                      <td className={`${TD} tabular-nums`}>
                        {o.members_count}
                        {o.removed_count > 0 && (
                          <span className="text-paper-hint"> (+{o.removed_count} исключено)</span>
                        )}
                      </td>
                      <td className={`${TD} text-right`}>
                        <Button variant="outline" size="sm"
                                onClick={() => { setSelected(o); setNote(null); setError(null); }}>
                          Управлять
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        )}
      </section>

      {selected && (
        <section data-testid="org-manage">
          <div className="flex items-center gap-3 mb-3">
            <h3 className={TITLE}>{selected.name}</h3>
            {/* Идентификатор виден: он нужен в обращениях и в логах, и это
                единственное место, где его можно прочитать. */}
            <code className={HINT}>{selected.id}</code>
            <div className="flex-1" />
            <Button variant="outline" size="sm" onClick={() => setSelected(null)}>Закрыть</Button>
          </div>

          {members && (
            <Card className="overflow-hidden mb-4">
              <table data-testid="org-members" className="w-full text-sm">
                <thead>
                  <tr className={HEAD_ROW}>
                    <th className={TH}>Почта</th><th className={TH}>Роль</th>
                    <th className={`${TH} text-right`}>Минуты</th><th className={`${TH} text-right`}>Протоколы</th>
                    <th className={TH}> </th>
                  </tr>
                </thead>
                <tbody className="tabular-nums">
                  {members.map((m) => {
                    // Расход — у действующей записи: у снова добавленного человека
                    // есть и прежняя, исключённая, и минуты не должны удваиваться.
                    const u = m.status === 'active' ? usage?.members?.find((x) => x.email === m.email) : null;
                    return (
                      <tr key={m.id || `${m.email}-${m.status}`} className={`${ROW} ${m.status === 'removed' ? 'opacity-50' : ''}`}>
                        <td className={TD}>{m.email}</td>
                        <td className={`${TD} text-paper-ink-3`}>
                          {m.role === 'owner' ? 'плательщик' : (m.status === 'removed' ? 'исключён' : 'участник')}
                        </td>
                        <td className={`${TD} text-right`}>{u ? Math.round(u.minutes) : '—'}</td>
                        <td className={`${TD} text-right`}>{u ? u.summaries : '—'}</td>
                        <td className={`${TD} text-right`}>
                          {m.role !== 'owner' && m.status === 'active' && (
                            <Button variant="outline" size="sm" className="text-paper-accent"
                                    aria-label={`Исключить ${m.email}`}
                                    onClick={() => {
                                      if (!window.confirm(`Исключить ${m.email} из «${selected.name}»? `
                                        + 'Общий пакет для него закончится, записи в его Истории останутся у него.')) return;
                                      run(() => billingService.adminRemoveOrgMember(selected.id, m.email),
                                          (o) => `Исключён ${o.email}`);
                                    }}>
                              Исключить
                            </Button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </Card>
          )}

          <Card className="p-4 flex flex-col gap-3">
            <div className="flex flex-wrap items-center gap-2">
              <span className="w-44 text-paper-ink-3">Привязать почту</span>
              {field(member, setMember, 'почта сотрудника', 'Почта сотрудника')}
              <Button size="sm" className={ACCENT_BTN}
                      onClick={() => run(
                        () => billingService.adminAddOrgMember(selected.id, member),
                        (o) => `Добавлен ${o.email}`)}>
                Привязать
              </Button>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <span className="w-44 text-paper-ink-3">Сменить плательщика</span>
              {field(newOwner, setNewOwner, 'почта нового плательщика', 'Почта нового плательщика')}
              <Button size="sm" variant="outline"
                      onClick={() => run(
                        () => billingService.adminChangeOrgOwner(selected.id, newOwner),
                        (o) => `Плательщик: ${o.owner_email}`)}>
                Сменить
              </Button>
            </div>
            <p className={HINT}>
              Прежний владелец остаётся в составе обычным участником: его записи и расход никуда не деваются.
            </p>
          </Card>
        </section>
      )}

      <section>
        <div className="flex items-center gap-3 mb-3">
          <h3 className={TITLE}>Завести организацию поверх существующей подписки</h3>
        </div>
        <Card className="p-4 flex flex-col gap-3">
          <p className={HINT}>
            Для тех, кто оплатил до появления организаций: активации у них уже не будет.
            Подписку это действие не трогает — ни срок, ни цену.
          </p>
          <div className="flex flex-wrap items-center gap-2">
            {field(form.owner_email, (v) => setForm({ ...form, owner_email: v }),
                   'почта плательщика', 'Почта плательщика')}
            {field(form.name, (v) => setForm({ ...form, name: v }), 'название', 'Название организации')}
            {field(form.inn, (v) => setForm({ ...form, inn: v }), 'ИНН', 'ИНН')}
            <Button size="sm" className={ACCENT_BTN}
                    onClick={() => run(() => billingService.adminCreateOrganization(form),
                                       (o) => `Организация ${o.name} заведена`)}>
              Завести
            </Button>
          </div>
        </Card>
      </section>

      {note && <div data-testid="admin-org-result" className={NOTE_BOX}>{note}</div>}
      {error && <div data-testid="admin-org-error" className={ERROR_BOX}>{error}</div>}
    </div>
  );
}
